import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { useRouter } from "next/router"
import SupplierList from "../SupplierList/SupplierList"
import * as styles from "./SupplierSearch.module.scss"
import SupplierSearchFilters from "./SupplierSearchFilters"
import SupplierSearchFiltersOverview from "./SupplierSearchFiltersOverview"
import cleanSearchParams from "@lib/cleanSearchParams"
import filterObject from "@lib/filterObject"
import useMediaQuery from "@lib/useMediaQuery"
import { DEFAULT_INITIAL_LOAD_NUM_SUPPLIERS } from "@data/constants"
import lambdas from "@data/lambdas"
import * as actions from "@store/actions"
import OnboardingSpinner from "@components/global/OnboardingSpinner/OnboardingSpinner"
import SavedSuppliersModal from "@components/global/SavedSuppliersModal/SavedSuppliersModal"
import Button from "@components/objects/Button/Button"
import gridViewButton from "@images/icons/gridViewButton.svg"
import listViewButtonActive from "@images/icons/listViewButtonActive.svg"

const { SUPPLIER_SEARCH } = lambdas[process.env.NEXT_PUBLIC_ENV]

const SupplierSearch = ({
  paramLoad,
  paramHasLoaded,
  staticData,
  currentParty,
  isSearchParamsUpdated,
  loadBrowserLocation,
  empty,
}) => {
  // Resize control
  const isMobile = useMediaQuery(767)
  const router = useRouter()
  const searchParams = cleanSearchParams(router.query)

  // filter control
  const defaultFilters = {
    first: DEFAULT_INITIAL_LOAD_NUM_SUPPLIERS,
    orderBy: "rank",
    order: "DESC",
    skip: 0,
  }
  const [supplierCount, updateSupplierCount] = useState()
  const [suppliers, updateSuppliers] = useState(undefined)
  const [filters, updateFilters] = useState({ ...defaultFilters, ...searchParams })

  const [isOnboardingLoading, updateIsOnboardingLoading] = useState(searchParams?.loading === "true")

  const [loadMoreActive, updateLoadMoreActive] = useState(true)

  // consts

  const virtualCategory = staticData?.categoryOptions?.find((el) => el.virtual)

  const initialLoad = async () => {
    // prevent geolocate if ids are present from moodboard or in Dev or mobile
    if ((searchParams.ids && searchParams.ids.length) || process.env.NEXT_PUBLIC_ENV === "dev" || isMobile) {
      loadBrowserLocation(true)
    }
    await getSuppliers(filters)
  }

  useEffect(() => {
    initialLoad()
  }, [])

  const updateURL = (currParams) => {
    const allowedParams = [
      "category",
      "name",
      "location",
      "services",
      "subservices",
      "style",
      "bundle",
      "pricerange",
      "ids",
    ]
    const cleanParams = filterObject(currParams, allowedParams)

    // store in redux
    paramLoad(cleanParams)

    // construct url format
    const urlParams = Object.keys(cleanParams)
      .map(
        (k) =>
          `${encodeURIComponent(k)}=${
            typeof cleanParams[k] === "object" ? JSON.stringify(cleanParams[k]) : cleanParams[k]
          }`
      )
      .join("&")

    // update url
    window.history.replaceState(null, null, `/suppliers-search/${urlParams ? `?${urlParams}` : ""}`)
  }

  const getSuppliers = async (currParams, append = false) => {
    // reset suppliers object to load spinner

    if (!append) {
      updateSuppliers(null)
      updateLoadMoreActive(true)
    }
    const initParam = { ...defaultFilters, ...currParams }

    try {
      const response = await fetch(SUPPLIER_SEARCH, {
        method: "POST",
        body: JSON.stringify({
          params: initParam,
        }),
      })

      if (response.ok) {
        await response.json().then((data) => {
          const newSuppliers =
            append && suppliers ? [...suppliers, ...data.result.allSuppliers] : data.result.allSuppliers

          updateSuppliers(newSuppliers)
          updateSupplierCount(data.result._allSuppliersMeta.count)
          updateIsOnboardingLoading(false)
          updateLoadMoreActive(data.result._allSuppliersMeta.count - newSuppliers.length > 0)
        })
      } else {
        alert(
          "There has been an error, please refresh the page and try again. If it persists please contact us at supplier@houseofpartyplanning.com"
        )
      }
    } catch (err) {
      console.log(err)
      alert(
        "There has been an error, please refresh the page and try again. If it persists please contact us at supplier@houseofpartyplanning.com"
      )
    }
  }

  const loadMore = async (page) => {
    if (suppliers.length) {
      const max = DEFAULT_INITIAL_LOAD_NUM_SUPPLIERS
      await getSuppliers({ ...filters, first: max, skip: max * page }, true)
      updateLoadMoreActive(supplierCount - max * page > 0)
    }
  }

  const updateResults = async (newFilters = {}) => {
    await updateFilters(newFilters)
    await updateURL(newFilters)
    await getSuppliers(newFilters)
  }

  return (
    <section className={`${styles.SupplierSearch}`}>
      <div className={styles.wrapper}>
        <div className={styles.grid}>
          <aside className={styles.searchSidebar}>
            <SupplierSearchFilters
              updateResults={updateResults}
              filters={filters}
              params={searchParams}
              resultsView="grid"
            />
          </aside>
          <div className={styles.searchArea}>
            <div className={styles.searchHeader}>
              <div className={styles.headerTop}>
                <SupplierSearchFiltersOverview updateResults={updateResults} filters={filters} />
              </div>
              <div className={styles.headerBottom}>
                <span className={styles.resultsCount} />
                {!isMobile && (
                  <div className={styles.viewButtons}>
                    <Button
                      className={styles.viewButton}
                      design="image"
                      text="Pictures"
                      image={gridViewButton}
                      link="/moodboard-search"
                    />
                    <Button className={styles.viewButton} design="image" text="List" image={listViewButtonActive} />
                  </div>
                )}
              </div>
            </div>
            <div className={styles.scrollingSection}>
              <SupplierList
                viewStyle="grid"
                suppliers={suppliers}
                empty={empty}
                updateResults={() =>
                  updateResults(
                    virtualCategory?.value && filters.category === virtualCategory.value ? virtualCategory.value : ""
                  )
                }
                clearAllFilters={() => updateResults()}
                loadFunc={loadMore}
                hasMore={loadMoreActive}
                pagination
                autoFeature
              />
            </div>
          </div>
        </div>
      </div>

      <SavedSuppliersModal currentParty={currentParty} />
      <OnboardingSpinner
        active={isOnboardingLoading}
        message="You’re ready to go!"
        submessage="We’re building your recommendations with lots of suppliers"
      />
    </section>
  )
}

const mapStateToProps = (state) => ({
  currentParty: state.party.currentParty,
  staticData: state.staticData.data,
  search: state.search.parameters,
  isSearchParamsUpdated: state.search.isUpdated,
})

const mapDispatchToProps = (dispatch) => ({
  paramLoad: (parameters) => dispatch(actions.paramLoad(parameters)),
  paramHasLoaded: () => dispatch(actions.paramHasLoaded()),
  partyUpdate: (party) => dispatch(actions.partyUpdate(party)),
  loadBrowserLocation: () => dispatch(actions.loadBrowserLocation()),
})

export default connect(mapStateToProps, mapDispatchToProps)(SupplierSearch)
